import type { AgentDialogue, NotificationContext } from '@/infra/sse/conversation/payloads'
import type { ToolInput } from '@/features/events/shared/types/tool-inputs'
import type { UserOption } from '@/features/events/shared/types/UserOption'

export const ERRORED_TOOL_NAME = 'errored'
const TOOL_EXIT_PLAN_MODE = 'ExitPlanMode'
const TOOL_ASK_USER_QUESTION = 'AskUserQuestion'

interface NotificationResolution {
  readonly notificationContext: NotificationContext | null
  readonly eventType: string
  readonly toolName: string | null
}

export function resolveNotificationContext(
  rawContext: Record<string, unknown> | null,
): NotificationResolution {
  if (!rawContext) {
    return buildErroredResult('Failed to deliver agent request', null)
  }

  const toolName = typeof rawContext['tool_name'] === 'string' ? rawContext['tool_name'] : null
  const rawToolInput = rawContext['tool_input']
  const rawDialogue = rawContext['agent_dialogue']
  const agentDialogue =
    typeof rawDialogue === 'object' && rawDialogue !== null
      ? buildAgentDialogue(rawDialogue as Record<string, unknown>)
      : null

  const toolInput = parseToolInput(rawToolInput)
  if (toolInput === null) {
    return buildErroredResult('Failed to deliver tool use execution details', agentDialogue)
  }

  if (toolName === TOOL_EXIT_PLAN_MODE) return validateAgentPlan(toolName, toolInput, agentDialogue)
  if (toolName === TOOL_ASK_USER_QUESTION)
    return validateAgentQuestion(toolName, toolInput, agentDialogue)
  return validateSchedulableTool(toolName, toolInput, agentDialogue)
}

/** Validates raw tool input is a non-null, non-array object. */
function parseToolInput(raw: unknown): Record<string, unknown> | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return null
  return raw as Record<string, unknown>
}

function buildAgentDialogue(raw: Record<string, unknown>): AgentDialogue | null {
  const agentPrompt = typeof raw['agent_prompt'] === 'string' ? raw['agent_prompt'] : null
  const rawOptions = Array.isArray(raw['user_options']) ? raw['user_options'] : null
  if (!agentPrompt || !rawOptions) return null

  const userOptions: UserOption[] = rawOptions.filter(
    (opt): opt is UserOption =>
      typeof opt === 'object' &&
      opt !== null &&
      typeof (opt as Record<string, unknown>)['index'] === 'number' &&
      typeof (opt as Record<string, unknown>)['option'] === 'string',
  )

  if (userOptions.length === 0) return null
  return { agentPrompt, userOptions }
}

function buildErroredResult(
  errorMessage: string,
  agentDialogue: AgentDialogue | null,
): NotificationResolution {
  return {
    notificationContext: {
      toolName: ERRORED_TOOL_NAME,
      toolInput: { error: errorMessage } as ToolInput,
      agentDialogue,
    },
    eventType: 'ERRORED_EVENT',
    toolName: ERRORED_TOOL_NAME,
  }
}

function validateAgentPlan(
  toolName: string,
  toolInput: Record<string, unknown>,
  agentDialogue: AgentDialogue | null,
): NotificationResolution {
  if (
    Object.keys(toolInput).length > 0 &&
    !('plan' in toolInput && typeof toolInput['plan'] === 'string')
  ) {
    return buildErroredResult('Failed to deliver plan details', agentDialogue)
  }
  if (agentDialogue === null) {
    return {
      notificationContext: {
        toolName,
        toolInput: toolInput as ToolInput,
        agentDialogue: null,
      },
      eventType: 'ERRORED_EVENT',
      toolName,
    }
  }
  return {
    notificationContext: { toolName, toolInput: toolInput as ToolInput, agentDialogue },
    eventType: 'PLAN_APPROVAL_REQUEST',
    toolName,
  }
}

function validateAgentQuestion(
  toolName: string,
  toolInput: Record<string, unknown>,
  agentDialogue: AgentDialogue | null,
): NotificationResolution {
  if (
    Object.keys(toolInput).length > 0 &&
    !('questions' in toolInput && Array.isArray(toolInput['questions']))
  ) {
    return buildErroredResult('Failed to deliver agent questions', agentDialogue)
  }
  return {
    notificationContext: { toolName, toolInput: toolInput as ToolInput, agentDialogue },
    eventType: 'AGENT_QUESTIONS',
    toolName,
  }
}

function validateSchedulableTool(
  toolName: string | null,
  toolInput: Record<string, unknown>,
  agentDialogue: AgentDialogue | null,
): NotificationResolution {
  if (agentDialogue === null) {
    return {
      notificationContext: {
        toolName: toolName ?? ERRORED_TOOL_NAME,
        toolInput: toolInput as ToolInput,
        agentDialogue: null,
      },
      eventType: 'ERRORED_EVENT',
      toolName,
    }
  }
  return {
    notificationContext: {
      toolName: toolName ?? ERRORED_TOOL_NAME,
      toolInput: toolInput as ToolInput,
      agentDialogue,
    },
    eventType: 'TOOL_PERMISSION_REQUEST',
    toolName,
  }
}
